import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Chip } from '@mui/material';
import { Send, CheckCircle, Award } from 'lucide-react';
import toast from 'react-hot-toast';

const HEADING = '#fafafa';
const BODY = '#d4d4d8';
const SECONDARY = '#a1a1aa';
const GOLD = '#fbbf24';
const GREEN = '#4ade80';
const CARD_RAD = '14px';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

// Capstone lessons are graded, first-class deliverables — not just another
// assignment — so submitting one creates/updates a CapstoneSubmission record
// that career matching and mentor review can query later.
const CapstoneSubmission = ({ courseId, sectionIndex, lessonIndex, onSubmitted }) => {
  const [text, setText] = useState('');
  const [artifactUrl, setArtifactUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim() && !artifactUrl.trim()) {
      toast.error('Add your write-up or a link to the work before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/capstones`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseId,
          sectionIndex,
          lessonIndex,
          submissionText: text.trim() || undefined,
          artifactUrl: artifactUrl.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Submission failed');
      setSubmitted(true);
      toast.success('Capstone submitted — a mentor will review it');
      onSubmitted?.();
    } catch (err) {
      toast.error(err.message || 'Failed to submit capstone');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 3, p: { xs: 2, md: 2.5 }, border: '1px solid rgba(99,102,241,0.2)', borderRadius: CARD_RAD, bgcolor: 'rgba(99,102,241,0.04)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Award size={16} color={GOLD} />
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 15 }}>Ship Your Capstone</Typography>
        {submitted && (
          <Chip icon={<CheckCircle size={12} />} label="Submitted for review" size="small"
            sx={{ bgcolor: 'rgba(74,222,128,0.1)', color: GREEN, '& .MuiChip-icon': { color: GREEN } }} />
        )}
      </Box>
      <Typography sx={{ color: SECONDARY, fontSize: 13, mb: 2, lineHeight: 1.6 }}>
        This is graded evidence — it feeds your verified skills and goes to a mentor for review, not just a completion checkbox.
      </Typography>
      <TextField
        multiline
        rows={5}
        fullWidth
        placeholder="Describe what you built, the decisions you made, and why…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={submitted}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '10px', color: BODY,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
          },
        }}
      />
      <TextField
        fullWidth
        placeholder="Link to the artifact (repo, doc, deck, deployed app)…"
        value={artifactUrl}
        onChange={(e) => setArtifactUrl(e.target.value)}
        disabled={submitted}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '10px', color: BODY,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
          },
        }}
      />
      <Button
        startIcon={<Send size={16} />}
        onClick={handleSubmit}
        disabled={submitting || submitted}
        variant="contained"
        sx={{
          bgcolor: submitted ? 'rgba(74,222,128,0.15)' : 'rgba(99,102,241,0.2)',
          color: submitted ? GREEN : '#a5b4fc',
          border: submitted ? 'none' : '1px solid rgba(99,102,241,0.35)',
          textTransform: 'none', borderRadius: '10px', fontWeight: 700,
          boxShadow: 'none', '&:hover': { bgcolor: 'rgba(99,102,241,0.3)' },
        }}
      >
        {submitted ? 'Submitted' : submitting ? 'Submitting…' : 'Submit Capstone'}
      </Button>
    </Box>
  );
};

export default CapstoneSubmission;
